import { truncateAddress } from "@aptos-labs/ts-sdk";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";

import { Icon } from "@components/Icon";

import { useMedia } from "@hooks/useMedia";
import { useStores } from "@stores";
import { DIALOG_TYPE } from "@stores/DialogStore";

import { Button, Image, Text } from "@src/ui-kit";

export const Wallet = observer(() => {
  const { t } = useTranslation();
  const { accountStore, dialogStore, geoFenceStore, isAppReady } = useStores();

  const wallet = accountStore.wallet?.account;
  const isConnecting =
    accountStore.wallet?.isLoading ||
    (accountStore.wallet?.connected && !accountStore.isAuthenticated && !accountStore.whitelistDenied);

  const { desktop } = useMedia();

  const handleConnectWallet = () => {
    if (geoFenceStore.isRestricted) return;
    accountStore.initConnectionFlow();
  };

  const openSideBar = () => {
    if (dialogStore.isVisible(DIALOG_TYPE.ASSETS)) {
      dialogStore.close(DIALOG_TYPE.ASSETS);
    } else {
      dialogStore.open(DIALOG_TYPE.ASSETS);
    }
  };

  if (isConnecting || !isAppReady) {
    return (
      <Button disabled>
        <Icon icon="ri-loader-2-line" size={16} />
        <Text font="button">{t("common.connecting")}</Text>
      </Button>
    );
  }

  if (!accountStore.isAuthenticated) {
    return (
      <Button
        data-testid="app:header:connect-button"
        disabled={geoFenceStore.isRestricted || accountStore.whitelistDenied}
        onClick={handleConnectWallet}
      >
        <Text font="button">
          {geoFenceStore.isRestricted
            ? t("geoFence.connectBlocked")
            : accountStore.whitelistDenied
              ? t("whitelist.title")
              : t("common.connect")}
        </Text>
      </Button>
    );
  }

  const WalletIcon = accountStore.wallet?.wallet?.icon;
  const isAssetsSidebarOpen = dialogStore.isVisible(DIALOG_TYPE.ASSETS);

  return (
    <>
      {desktop ? (
        <Button data-testid="app:header:wallet-address-button" variant="classic" onClick={openSideBar}>
          {WalletIcon && <Image alt="wallet" height={16} src={WalletIcon} width={16} />}
          <Text font="button">
            {wallet?.ansName || truncateAddress((wallet?.address ?? "").toString()) || t("common.unknown")}
          </Text>
          <ArrowIconWrapper isOpen={isAssetsSidebarOpen}>
            <Icon icon="ri-arrow-down-s-line" size={16} />
          </ArrowIconWrapper>
        </Button>
      ) : (
        <Button
          aria-label={t("wallet.openWalletSidebar", "Open wallet panel")}
          style={{ width: 32, height: 32 }}
          variant="classic"
          onClick={openSideBar}
        >
          <Icon icon="ri-account-circle-line" size={16} />
        </Button>
      )}
    </>
  );
});

const ArrowIconWrapper = styled.span<{ isOpen: boolean }>`
  display: inline-flex;
  transform: ${({ isOpen }) => (isOpen ? "rotate(-90deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease-in-out;
`;
